// @ts-ignore
import Color from 'color';
import _ from 'lodash';
import parseUnit from 'parse-unit';
import { EmbedResponsiveAspectRatio, EmbedResponsiveAspectRatios, makeTheme, Spacer, Spacers, Theme, ThemeBase } from './types';

const fromPercentage = (percentage: string): number => parseUnit(percentage)[0] * 0.01;

const multiplyWithUnits = (valueWithUnits: string, multiplicand: number): string => {
    let [size, unit] = parseUnit(valueWithUnits);
    return `${Math.ceil(size * multiplicand)}${unit}`
};

const embedAspectRatiosFromBootstrap = (embedAspectRatios: string): EmbedResponsiveAspectRatios => _(embedAspectRatios).split(' ').chunk(2).map((r) => ({ x: Number(r[0]), y: Number(r[1]) } as EmbedResponsiveAspectRatio)).value();

//const p = (themeVar: keyof ThemeBase) => (theme) => theme[themeVar]; 

export const base: ThemeBase = {
    white: "#fff",
    gray100: "#f8f9fa",
    gray200: "#e9ecef",
    gray300: "#dee2e6",
    gray400: "#ced4da",
    gray500: "#adb5bd",
    gray600: "#6c757d",
    gray700: "#495057",
    gray800: "#343a40",
    gray900: "#212529",
    black: "#000",

    grays: ({ gray100, gray200, gray300, gray400, gray500, gray600, gray700, gray800, gray900 }) => ({
        100: gray100,
        200: gray200,
        300: gray300,
        400: gray400,
        500: gray500,
        600: gray600,
        700: gray700,
        800: gray800,
        900: gray900
    }),

    blue: "#0d6efd",
    indigo: "#6610f2",
    purple: "#6f42c1",
    pink: "#d63384",
    red: "#dc3545",
    orange: "#fd7e14",
    yellow: "#ffc107",
    green: "#198754",
    teal: "#20c997",
    cyan: "#0dcaf0",

    colors: ({ blue, indigo, purple, pink, red, orange, yellow, green, teal, cyan, white, gray600, gray800 }) => ({
        blue,
        indigo,
        purple,
        pink,
        red,
        orange,
        yellow,
        green,
        teal,
        cyan,
        white,
        gray: gray600,
        grayDark: gray800
    }),

    primary: ({ blue }) => blue,
    secondary: ({ gray600 }) => gray600,
    success: ({ green }) => green,
    info: ({ cyan }) => cyan,
    warning: ({ yellow }) => yellow,
    danger: ({ red }) => red,
    light: ({ gray100 }) => gray100,
    dark: ({ gray800 }) => gray800,

    themeColors: ({ primary, secondary, success, info, warning, danger, light, dark }) => ({
        primary,
        secondary,
        success,
        info,
        warning,
        danger,
        light,
        dark
    }),

    //themeColorInterval: fromPercentage(bootstrapStyle.themeColorInterval),
    spacer: "1rem",

    spacers: ({ spacer }) => {
        let [val, unit] = parseUnit(spacer);

        return ([
            0,
            `${val * .25}${unit}`,
            `${val * .5}${unit}`,
            spacer,
            `${val * 1.5}${unit}`,
            `${val * 3}${unit}`
        ] as Spacer[]) as Spacers;
    },

    sizes: {
        25: '25%',
        50: '50%',
        75: '75%',
        100: '100%',
        auto: 'auto'
    },

    bodyBg: ({ white }) => white,
    bodyColor: ({ gray900 }) => gray900,

    linkColor: ({ themeColors }) => themeColors.primary,
    linkDecoration: "underline",
    linkHoverColor: ({ linkColor }) => Color(linkColor).darken(0.15).toString(),
    linkHoverDecoration: "null",
    //emphasizedLinkHoverDarkenPercentage: fromPercentage(bootstrapStyle.emphasizedLinkHoverDarkenPercentage),
    paragraphMarginBottom: "1rem",

    gridBreakPoints: {
        xs: 0,
        sm: "576px",
        md: "768px",
        lg: "992px",
        xl: "1200px",
        xxl: "1400px"
    },

    containerMaxWidths: {
        sm: "540px",
        md: "720px",
        lg: "960px",
        xl: "1140px",
        xxl: "1320px"
    },

    lineHeightLg: "1.25",
    lineHeightSm: "2",

    borderWidth: "1px",
    borderColor: "$gray-300",

    //$border - radius: .25rem!default ;
    //$border - radius - lg: .3rem!default ;
    //$border - radius - sm: .2rem!default ;
    borderRadius: ".375rem",
    borderRadiusLg: ".5rem",
    borderRadiusSm: ".25rem",

    //$rounded - pill: 50rem!default ;
    rounderPill: "",

    //$box - shadow - sm: 0 .125rem .25rem rgba($black, .075)!default ;
    //$box - shadow: 0 .5rem 1rem rgba($black, .15)!default ;
    //$box - shadow - lg: 0 1rem 3rem rgba($black, .175)!default ;
    boxShadowSm: ({ black }) => `0 .125rem .25rem ${Color(black).alpha(.075)}`,
    boxShadow: ({ black }) => `0 .5rem 1rem ${Color(black).alpha(.15)}`,
    boxShadowLg: ({ black }) => `0 1rem 3rem ${Color(black).alpha(.175)}`,

    //$component - active - color: $white!default ;
    //$component - active - bg - color("primary")!default ;
    componentActiveColor: ({ white }) => white,
    componentActiveBg: ({ themeColors }) => themeColors.primary,

    //$caret - width: .3em!default ,
    //$caret - vertical - align: $caret - width * .85!default ,
    //$caret - spacing: $caret - width * .85!default ,
    caretWidth: ".3em",
    caretVerticalAlign: ({ caretWidth }) => multiplyWithUnits(caretWidth as string, .85),
    caretSpacing: ({ caretWidth }) => multiplyWithUnits(caretWidth as string, .85),
    //$transition - base: all .2s ease -in -out!default ;
    //$transition - fade: opacity .15s linear!default ;
    //$transition - collapse: height .35s ease!default ;
    transitionBase: "all .2s ease-in-out",
    transitionFade: "opacity .15s linear",
    transitionCollapse: "height .35s ease",

    //$embed - responsive - aspect - ratios: ()!default ;
    //$embed - responsive - aspect - ratios: join(
    //    (
    //        (21 9),
    //    (16 9),
    //(4 3),
    //(1 1),
    //  ),
    //$embed - responsive - aspect - ratios
    //);
    embedResponsiveAspectRatios: [
        { x: 21, y: 9 },
        { x: 16, y: 9 },
        { x: 4, y: 3 },
        { x: 1, y: 1 },
    ],

    // Typography
    //
    // Font, line-height, and color for body text, headings, and more.
    fontFamilySansSerif: 'system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", "Noto Sans", "Liberation Sans", Arial, sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol", "Noto Color Emoji"',
    //fontFamilySerif: 'SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
    fontFamilyMonospace: 'SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
    fontFamilyBase: ({ fontFamilySansSerif }) => fontFamilySansSerif,

    //$font- size - base:              1rem!default ; // Assumes the browser default, typically `16px`
    //$font - size - lg: $font - size - base * 1.25!default ;
    //$font - size - sm: $font - size - base * .875!default ;
    fontSizeBase: "1rem",
    fontSizeLarge: ({ fontSizeBase }) => multiplyWithUnits(fontSizeBase as string, 1.25),
    fontSizeSmall: ({ fontSizeBase }) => multiplyWithUnits(fontSizeBase as string, .85),

    //$font - weight - lighter: lighter!default ;
    //$font - weight - light: 300!default ;
    //$font - weight - normal: 400!default ;
    //$font - weight - bold: 700!default ;
    //$font - weight - bolder: bolder!default ;
    fontWeightLighter: "lighter",
    fontWeightLight: 300,
    fontWeightNormal: 400,
    fontWeightBold: 700,
    fontWeightBolder: "bolder",

    //$font - weight - base: $font - weight - normal!default ;
    //$line - height - base: 1.5!default ;
    fontWeightBase: ({ fontWeightNormal }: Theme) => fontWeightNormal,
    lineHeightBase: "1.5",

    //$h1 - font - size: $font - size - base * 2.5!default ;
    //$h2 - font - size: $font - size - base * 2!default ;
    //$h3 - font - size: $font - size - base * 1.75!default ;
    //$h4 - font - size: $font - size - base * 1.5!default ;
    //$h5 - font - size: $font - size - base * 1.25!default ;
    //$h6 - font - size: $font - size - base!default ;
    h1FontSize: ({ fontSizeBase }) => multiplyWithUnits(fontSizeBase as string, 2.5),
    h2FontSize: ({ fontSizeBase }) => multiplyWithUnits(fontSizeBase as string, 2),
    h3FontSize: ({ fontSizeBase }) => multiplyWithUnits(fontSizeBase as string, 1.75),
    h4FontSize: ({ fontSizeBase }) => multiplyWithUnits(fontSizeBase as string, 1.5),
    h5FontSize: ({ fontSizeBase }) => multiplyWithUnits(fontSizeBase as string, 1.25),
    h6FontSize: ({ fontSizeBase }) => fontSizeBase,

    //$headings - margin - bottom: $spacer / 2!default ;
    //$headings - font - family: null!default ;
    //$headings - font - weight: 500!default ;
    //$headings - line - height: 1.2!default ;
    //$headings - color: null!default ;
    headingsMarginBottom: ({ spacer }) => multiplyWithUnits(spacer as string, .5),
    headingsFontFamily: "null",
    headingsFontWeight: 500,
    headingsLineHeight: "1.2",
    headingsColor: "null",

    //$display1 - size: 6rem!default ;
    //$display2 - size: 5.5rem!default ;
    //$display3 - size: 4.5rem!default ;
    //$display4 - size: 3.5rem!default ;
    display1Size: "6rem",
    display2Size: "5.5rem",
    display3Size: "4.5rem",
    display4Size: "3.5rem",

    //$display1 - weight: 300!default ;
    //$display2 - weight: 300!default ;
    //$display3 - weight: 300!default ;
    //$display4 - weight: 300!default ;
    //$display - line - height: $headings - line - height!default ;
    display1Weight: 300,
    display2Weight: 300,
    display3Weight: 300,
    display4Weight: 300,
    displayLineHeight: ({ headingsLineHeight }) => headingsLineHeight,


    /*
 // Navs

$nav-link-padding-y:                .5rem !default;
$nav-link-padding-x:                1rem !default;
$nav-link-disabled-color:           $gray-600 !default;

$nav-tabs-border-color:             $gray-300 !default;
$nav-tabs-border-width:             $border-width !default;
$nav-tabs-border-radius:            $border-radius !default;
$nav-tabs-link-hover-border-color:  $gray-200 $gray-200 $nav-tabs-border-color !default;
$nav-tabs-link-active-color:        $gray-700 !default;
$nav-tabs-link-active-bg:           $body-bg !default;
$nav-tabs-link-active-border-color: $gray-300 $gray-300 $nav-tabs-link-active-bg !default;

$nav-pills-border-radius:           $border-radius !default;
$nav-pills-link-active-color:       $component-active-color !default;
$nav-pills-link-active-bg:          $component-active-bg !default;

$nav-divider-color:                 $gray-200 !default;
$nav-divider-margin-y:              $spacer / 2 !default;
 
 */
    navTabsBorderColor: ({ gray300 }) => gray300,
    navTabsBorderWidth: ({ borderWidth }) => borderWidth,
    navTabsBorderRadius: ({ borderRadius }) => borderRadius,
    navTabsLinkHoverBorderColor: ({ gray200, navTabsBorderColor }) => `${gray200} ${gray200} ${navTabsBorderColor}` as string,
    navTabsLinkActiveColor: ({ gray700 }) => gray700,
    navTabsLinkActiveBg: ({ componentActiveColor }) => componentActiveColor,
    navTabsLinkActiveBorderColor: ({ componentActiveBg }) => componentActiveBg,
}

const theme = makeTheme(base) as Theme;

export default theme;